/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-01-29 10:00:00
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { reduxForm, Field, getFormValues, change, reset } from 'redux-form';
import { Layout, Card, Button, TextStyle, Modal, TextContainer, Link } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';

import { RenderSelect } from 'src/components/views/common/forms/render-select';
import { RenderDatePicker } from 'src/components/views/sync/sync-schedule-tab/render-date-picker';
import { RenderTime } from 'src/components/views/sync/sync-schedule-tab/render-time';
import { SYNC_LIST, SYNC_TYPE_FLOW_BEST_SELLING } from 'src/constants/sync';
import { APP_LANGUAGE_ENGLISH_VALUE, DATE_FORMAT } from 'src/constants/language-setting';
import {
  buildInitialSyncSchedule,
  validateSyncSchedule,
  buildSyncSchedule
} from 'src/helpers/sync-helper';
import { displayStoreDate } from 'src/helpers/date-time-helper';

import * as syncActions from 'actions/sync-actions';
import * as checklistActions from 'actions/dashboard/checklist-actions';

type Props = {
  syncSchedule: Object,
  syncTypeOptions: Array<{ value: string, label: string }>,
  saveSyncScheduleLoading: boolean,
  initialValues: Object,
  flowName: string,
  selectedDate: string,
  appLanguage: string,
  getSyncScheduleLoading: () => void,
  createSyncSchedule: (syncSchedule: Object) => void,
  updateField: (field: string, value: any) => void,
  resetForm: () => void
};

type ComposedProps = Props & WithI18nProps;

type State = {
  disableEdit: boolean,
  activeCollectionNotFoundModal: boolean,
  valueSelect: Object
};

class SyncScheduleTab extends Component<ComposedProps, State> {
  state = {
    disableEdit: false,
    valueSelect: null,
    activeCollectionNotFoundModal: false
  };

  componentDidMount() {
    const { getSyncSchedule } = this.props;

    getSyncSchedule();
  }

  componentWillReceiveProps(nextProps) {
    const { getSyncScheduleLoading } = this.props;
    const { getSyncScheduleLoading: getSyncScheduleLoadingNext, valueSelect, dispatch } = nextProps;
    if (getSyncScheduleLoading && !getSyncScheduleLoadingNext) {
      const { syncSchedule } = nextProps;
      if (!_.isEmpty(syncSchedule)) {
        this.setState({ disableEdit: true });
      } else {
        this.setState({ disableEdit: false });
      }
    }
    if (_.get(valueSelect, 'value') !== _.get(this.state.valueSelect, 'value')) {
      dispatch(change(`SyncScheduleTabForm`, 'flowName', valueSelect.value));
      this.setState({ valueSelect });
    }
  }

  renderLabelTitle = () => {
    const { syncSchedule, i18n, appLanguage } = this.props;
    const current = new Date().getTime() / 1000;

    if (_.isEmpty(syncSchedule) || current > syncSchedule.scheduleAt) {
      return i18n.translate('AdminHTML.Sync.SyncSchedule.noSchedule');
    } else {
      const formatDate = DATE_FORMAT[appLanguage].fullDateStartWithTime;
      const { flowName = '', scheduleAt = 0 } = syncSchedule;
      const flowLabel = SYNC_LIST[flowName];
      return i18n.translate('AdminHTML.Sync.SyncSchedule.hasSchedule', {
        nt_name: (
          <TextStyle variation="strong">
            {!_.isEmpty(flowLabel) ? i18n.translate(flowLabel) : ''}
          </TextStyle>
        ),
        nt_time: (
          <TextStyle variation="strong">{displayStoreDate(scheduleAt, formatDate)}</TextStyle>
        )
      });
    }
  };

  onChangeFlow = (flowName, options) => {
    this.props.onSelectValueSelect(options.find(v => v.value === flowName));
  };

  handleChangeModal = stateField => {
    const value = this.state[stateField] || false;
    this.setState({ [stateField]: !value });
  };

  render() {
    const {
      pristine,
      submitting,
      handleSubmit,
      i18n,
      selectedDate,
      syncTypeOptions,
      updateField,
      getSyncScheduleLoading,
      saveSyncScheduleLoading,
      isRemoveSyncSchedule,
      flowName,
      syncSchedule,
      emailSupport
    } = this.props;

    const { disableEdit, activeCollectionNotFoundModal } = this.state;

    const options = [
      ...[
        {
          value: '',
          label: i18n.translate('AdminHTML.Sync.SyncSchedule.noSyncType')
        }
      ],
      ...syncTypeOptions
    ];

    return (
      <div className="sync-schedule">
        <Card>
          <Card.Section>
            <Layout>
              <Layout.Section>
                <p style={{ marginBottom: '1.25rem' }}>{this.renderLabelTitle()}</p>
                <form onSubmit={this.handleDefaultSubmit}>
                  <div className="sync-select">
                    <Field
                      name="flowName"
                      label={i18n.translate('AdminHTML.Sync.SyncSchedule.syncType_Label')}
                      options={options}
                      component={RenderSelect}
                      disabled={disableEdit}
                      updateField={updateField}
                      onChange={newValue => {
                        this.onChangeFlow(newValue, options);
                      }}
                    />
                  </div>

                  <div className="sync-schedule-time">
                    <div className="item">
                      <RenderDatePicker
                        name="selectedDate"
                        updateField={updateField}
                        selectedDate={selectedDate}
                        disabled={_.isEmpty(flowName) || disableEdit}
                      />
                    </div>
                    <div className="item">
                      <Field
                        name="selectedTime"
                        label={i18n.translate('AdminHTML.Sync.SyncSchedule.time_Label')}
                        component={RenderTime}
                        updateField={updateField}
                        selectedDate={selectedDate}
                        isDisabled={_.isEmpty(flowName) || disableEdit}
                      />
                    </div>
                  </div>
                  <div className="sync-schedule-action">
                    {syncSchedule ? (
                      [
                        disableEdit ? (
                          <Button primary key="edit" onClick={this.changeEditState}>
                            {i18n.translate('AdminHTML.Sync.SyncSchedule.editSchedule')}
                          </Button>
                        ) : (
                          <Button
                            loading={!isRemoveSyncSchedule && saveSyncScheduleLoading}
                            primary
                            key="edit"
                            onClick={handleSubmit(this.handleSave.bind(this, false))}>
                            {i18n.translate('AdminHTML.Sync.SyncSchedule.saveSchedule')}
                          </Button>
                        ),
                        <Button
                          key="remove"
                          loading={isRemoveSyncSchedule && saveSyncScheduleLoading}
                          onClick={handleSubmit(this.handleSave.bind(this, true))}>
                          {i18n.translate('AdminHTML.Sync.SyncSchedule.removeSyncSchedule')}
                        </Button>
                      ]
                    ) : (
                      <Button
                        primary
                        disabled={
                          getSyncScheduleLoading ||
                          pristine ||
                          submitting ||
                          (_.isEmpty(flowName) && _.isEmpty(syncSchedule))
                        }
                        loading={!isRemoveSyncSchedule && saveSyncScheduleLoading}
                        onClick={handleSubmit(this.handleSave.bind(this, false))}>
                        {i18n.translate('AdminHTML.Sync.SyncSchedule.setSyncSchedule')}
                      </Button>
                    )}
                  </div>
                </form>
              </Layout.Section>
            </Layout>
          </Card.Section>
          <Modal
            large
            open={activeCollectionNotFoundModal}
            onClose={() => this.handleChangeModal('activeCollectionNotFoundModal')}
            title={
              <TextStyle>
                {i18n.translate('AdminHTML.Sync.Sync.modal2_Title', {
                  t1: (
                    <TextStyle variation="strong">
                      {i18n.translate('AdminHTML.Sync.Sync.modal2_Title_t1')}
                    </TextStyle>
                  )
                })}
              </TextStyle>
            }
            primaryAction={{
              content: i18n.translate('AdminHTML.Sync.Sync.modal2_PrimaryAction'),
              onAction: () => this.openLink('SyncDocBoostAllCollection')
            }}
            secondaryActions={[
              {
                content: i18n.translate('AdminHTML.Common.Action.cancel'),
                onAction: () => this.handleChangeModal('activeCollectionNotFoundModal')
              }
            ]}>
            <Modal.Section>
              <TextContainer>
                <p>
                  {i18n.translate('AdminHTML.Sync.Sync.modal2_Paragraph1', {
                    t1: (
                      <Link external url={URL['SyncDocBoostAllCollection']}>
                        {i18n.translate('AdminHTML.Sync.Sync.modal2_Paragraph1_t1')}
                      </Link>
                    )
                  })}
                </p>
                <p>
                  {i18n.translate('AdminHTML.Sync.Sync.modal2_Paragraph2', {
                    nt_email: <TextStyle variation="strong">{emailSupport}</TextStyle>
                  })}
                </p>
              </TextContainer>
            </Modal.Section>
          </Modal>
        </Card>
      </div>
    );
  }

  changeEditState = () => {
    this.setState({ disableEdit: false });
  };

  // Handle default form submit
  handleDefaultSubmit(e) {
    e.preventDefault();
  }

  discardChange = () => {
    const { resetForm } = this.props;
    resetForm();
  };

  async handleSave(isRemove, data) {
    const { saveSyncSchedule, i18n, trackEvent, getSyncSchedule, collectionBoostAllExists } =
      this.props;

    if (_.get(data, 'flowName') === SYNC_TYPE_FLOW_BEST_SELLING && !collectionBoostAllExists) {
      this.handleChangeModal('activeCollectionNotFoundModal');
      return;
    }

    // Update data before save
    if (validateSyncSchedule(data, i18n)) {
      const syncSchedule = buildSyncSchedule(data);

      if (isRemove) {
        syncSchedule.flowName = '';
      }

      const result = await saveSyncSchedule(syncSchedule);
      if ((result.type = 'SAVE_SYNC_SCHEDULE_SUCCESS')) {
        this.setState({ disableEdit: true });
      }

      trackEvent('csatFeedback', 'scheduleSync', 'csatFeedback_scheduleSync');
      getSyncSchedule();
    }
  }
}

const SyncSchedule = reduxForm({
  form: 'SyncScheduleTabForm', // a unique identifier for this form
  enableReinitialize: true
})(SyncScheduleTab);

const mapStateToProps = (state, ownProps) => {
  const syncSchedule = _.get(state.sync, 'syncSchedule', {});
  const formValues = getFormValues('SyncScheduleTabForm')(state);
  const tenantId = _.get(state.oauth, 'tenantApp.tenantId', '');
  const initialValues = buildInitialSyncSchedule(syncSchedule, ownProps.currentTime, tenantId);

  return {
    getSyncScheduleLoading: _.get(state.sync, 'getSyncScheduleLoading', false),
    saveSyncScheduleLoading: _.get(state.sync, 'saveSyncScheduleLoading', false),
    isRemoveSyncSchedule: _.get(state.sync, 'isRemoveSyncSchedule', false),
    syncSchedule,
    initialValues,
    flowName: _.get(formValues, 'flowName', ''),
    selectedDate: _.get(formValues, 'selectedDate', ''),
    appLanguage: _.get(state.oauth, 'tenantApp.appLanguage', APP_LANGUAGE_ENGLISH_VALUE),
    collectionBoostAllExists: _.get(state.sync, 'collectionBoostAllExists', false)
  };
};

const mapDispatchToProps = (dispatch, ownProps) => ({
  // Get sync schedule
  getSyncSchedule: () => dispatch(syncActions.getSyncSchedule(ownProps.i18n)),
  saveSyncSchedule: syncSchedule =>
    dispatch(syncActions.saveSyncSchedule(syncSchedule, ownProps.i18n)),
  updateField: (field, value) => dispatch(change(`SyncScheduleTabForm`, field, value)),
  resetForm: () => dispatch(reset('SyncScheduleTabForm')),
  trackEvent: (eventSource, eventName, label) =>
    dispatch(checklistActions.trackEvent(eventSource, eventName, label))
});

export default connect(mapStateToProps, mapDispatchToProps)(withI18n()(SyncSchedule));
